import {
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import MessageError from '../../../MessageError';
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { useRequestSelectiveProcessVacancy } from '../../../../services/hooks/SelectiveProcess/useRequestSelectiveProcessVacancy';
import { IReserved_vacancy } from '../../../../services/hooks/Registration/useGetRegistration';
import PcdAttachments from '../PcdAttachments';
import { IResponseIdName, useRequestQuery } from '../../../../services/hooks/globalQuery';
import React, { useMemo } from 'react';
import { CustomSelect } from '../../../CustomSelect';
import {
  useRequestResearchLines,
  useRequestThematicAxes,
} from '../../../../services/hooks/SelectiveProcess/useRequestRegistrationHierarchy';

interface IhookForms {
  medical_report?: FieldValues;
  modality_type?: string[];
  vacancy?: string;
  allow_filter_vacancy_tree?: string;
  research_line?: string;
  thematic_axis?: string;
  deficiency?: string;
  cid?: string;
  type_of_competition?: string;
  type_of_competition_id?: string;
  sub_type_of_competition?: string[] | null;
}

interface IOwnProps {
  hookForms: UseFormReturn<IhookForms>;
  selectiveProcessId: string;
  isCultureHouse: boolean;
  detailPcd?: IReserved_vacancy;
  onRemovePcdAttachment?: (id: string) => void;
  modalityAvailable?: IResponseIdName[];
  isFilterVacancyLevel?: boolean;
}

export enum ETypeOfCompetition {
  FULL_VACANCY = '1',
  SPECIAL_VACANCY = '2',
  UFC_SERVER_VACANCY = '3',
}

const DefaultVacancyForm: React.FC<IOwnProps> = ({
  hookForms,
  selectiveProcessId,
  isCultureHouse,
  detailPcd,
  onRemovePcdAttachment,
  modalityAvailable,
  isFilterVacancyLevel,
}) => {
  const vacancy = useRequestSelectiveProcessVacancy(selectiveProcessId, isCultureHouse);
  const subTypeCompetitions = useRequestQuery<IResponseIdName[]>({
    url: 'registration/sub-type-of-competition',
    key: ['list_sub_type_competition'],
    remove_pagination: true,
  });

  const selectedTypes = hookForms.watch('modality_type') ?? [];
  const selectedSubtype = hookForms.watch('sub_type_of_competition') ?? [];
  const selectedLevelId = hookForms.watch('allow_filter_vacancy_tree');
  const selectedResearchLineId = hookForms.watch('research_line');
  const selectedThematicAxisId = hookForms.watch('thematic_axis');
  const researchLines = useRequestResearchLines(selectiveProcessId, selectedLevelId);
  const thematicAxes = useRequestThematicAxes(selectedResearchLineId);
  const hasResearchLineCascade = !isCultureHouse && Boolean(researchLines.data?.length);
  const filteredVacancies = useRequestSelectiveProcessVacancy(selectiveProcessId, isCultureHouse, '', '', {
    level_id: selectedLevelId,
    research_line_id: selectedResearchLineId,
    thematic_axis_id: selectedThematicAxisId,
  });
  const selectedVacancy = vacancy.data?.find((item) => item.id === hookForms.watch('vacancy'));
  const showFullVacancies =
    !isFilterVacancyLevel || (selectedVacancy?.number_of_extensive_competition_vacancies ?? 0) > 0;
  const showReservedVacancies = !isFilterVacancyLevel || (selectedVacancy?.number_of_paa_vacancies ?? 0) > 0;
  const showUfcServerVacancies = isFilterVacancyLevel && (selectedVacancy?.number_of_ufc_server_vacancies ?? 0) > 0;
  const hasCompetitionOptions = showFullVacancies || showReservedVacancies || showUfcServerVacancies;

  const subTypeOfCompetitionList = useMemo(() => {
    return subTypeCompetitions.data
      ?.filter((item) => {
        const normalizedName = item.name.toLowerCase();
        const isBlackAndBrownCombined = normalizedName.includes('pret') && normalizedName.includes('pard');

        return isBlackAndBrownCombined || (!normalizedName.includes('pret') && !normalizedName.includes('pard'));
      })
      .map((item) => {
        const isChecked = selectedSubtype.includes(item.id);

        if (isFilterVacancyLevel) {
          return (
            <Radio key={item.id} value={item.id}>
              {item.name}
            </Radio>
          );
        }

        return (
          <Checkbox
            key={item.id}
            isChecked={isChecked}
            onChange={(e) => {
              const value = item.id;
              const current = hookForms.watch('sub_type_of_competition') ?? [];

              const newValues = e.target.checked ? [...current, value] : current.filter((v: string) => v !== value);

              hookForms.setValue('sub_type_of_competition', newValues, {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          >
            {item.name}
          </Checkbox>
        );
      });
  }, [hookForms, isFilterVacancyLevel, selectedSubtype, subTypeCompetitions.data]);

  const vacancyLevel = useMemo(() => {
    return Array.from(
      new Map(
        (vacancy.data ?? []).filter((item) => item.level?.id).map((item) => [item.level.id, item.level] as const),
      ).values(),
    );
  }, [vacancy.data]);

  const listVacancy = useMemo(() => {
    if (isFilterVacancyLevel) {
      return vacancy.data?.filter((item) => item.level?.id === hookForms.watch('allow_filter_vacancy_tree'));
    }

    return vacancy.data;
  }, [vacancyLevel, vacancy.data, isFilterVacancyLevel, hookForms.watch('allow_filter_vacancy_tree')]);
  const vacancyOptions = (hasResearchLineCascade ? filteredVacancies.data : listVacancy) ?? [];
  const showNoVacanciesMessage =
    Boolean(isFilterVacancyLevel && selectedThematicAxisId) &&
    !filteredVacancies.isLoading &&
    vacancyOptions.length === 0;

  const isModalityChecked = (modality: IResponseIdName) => {
    return selectedTypes.some((item) => {
      if (!item) return false;

      try {
        const parsed = JSON.parse(item);
        return parsed?.id === modality.id;
      } catch {
        return item.toString() === modality.id;
      }
    });
  };

  const renderPCDBlock = () => (
    <Stack gap={3}>
      <Stack gap={0} mt={3}>
        <FormControl>
          <FormLabel fontSize="14px" fontWeight="semibold">
            Envio de laudo - Pessoas com deficiência (PCD)
          </FormLabel>
          <Input {...hookForms.register('medical_report')} type="file" multiple w="fit-content" mt={1} />
          {hookForms.formState.errors?.medical_report && (
            <MessageError>{`${hookForms.formState.errors.medical_report.message}`}</MessageError>
          )}
        </FormControl>

        {Boolean(detailPcd?.attachments?.length) && (
          <Stack mt={2}>
            <PcdAttachments attachments={detailPcd?.attachments} onRemove={onRemovePcdAttachment} />
          </Stack>
        )}
      </Stack>

      <Stack gap={0} mt={3}>
        <FormControl>
          <FormLabel fontSize="14px" fontWeight="semibold">
            CID
          </FormLabel>
          <Input mt={1} {...hookForms.register('cid')} />
          {hookForms.formState.errors?.cid && (
            <MessageError>{`${hookForms.formState.errors.cid.message}`}</MessageError>
          )}
        </FormControl>
      </Stack>

      <Stack gap={0} mt={3}>
        <FormControl>
          <FormLabel fontSize="14px" fontWeight="semibold">
            Descrição da deficiência
          </FormLabel>
          <Textarea mt={1} {...hookForms.register('deficiency')} />
          {hookForms.formState.errors?.deficiency && (
            <MessageError>{`${hookForms.formState.errors.deficiency.message}`}</MessageError>
          )}
        </FormControl>
      </Stack>
    </Stack>
  );

  const renderPPPIQBlock = () => (
    <Stack mt={3}>
      <FormControl
        display="flex"
        flexDir="column"
        isInvalid={!!hookForms.formState.errors.sub_type_of_competition?.message}
      >
        <FormLabel fontSize="14px" fontWeight="semibold">
          {isFilterVacancyLevel
            ? 'Selecione abaixo o grupo ao qual você se autodeclara pertencente (escolha apenas uma opção):'
            : 'Selecione abaixo um ou mais grupos aos quais você se autodeclara pertencente:'}
        </FormLabel>

        {isFilterVacancyLevel ? (
          <RadioGroup
            value={selectedSubtype[0] ?? ''}
            onChange={(value) => {
              hookForms.setValue('sub_type_of_competition', [value], {
                shouldDirty: true,
                shouldValidate: true,
              });
            }}
          >
            <Stack>{subTypeOfCompetitionList}</Stack>
          </RadioGroup>
        ) : (
          subTypeOfCompetitionList
        )}

        <FormErrorMessage fontSize="0.78rem">
          {hookForms.formState.errors.sub_type_of_competition?.message?.toString()}
        </FormErrorMessage>
      </FormControl>
    </Stack>
  );

  return (
    <Stack>
      <Flex gap={5} align="center" flexWrap="wrap">
        {isFilterVacancyLevel && (
          <Stack w="100%" gap={0}>
            <FormLabel fontSize="14px" fontWeight="semibold">
              Curso:
            </FormLabel>
            <CustomSelect
              options={vacancyLevel}
              value={selectedLevelId || ''}
              disabled={vacancy.isLoading || vacancyLevel.length === 0}
              setValue={(value) => {
                hookForms.setValue('allow_filter_vacancy_tree', value);
                hookForms.setValue('research_line', '');
                hookForms.setValue('thematic_axis', '');
                hookForms.setValue('vacancy', '');
              }}
            />
          </Stack>
        )}
        {isFilterVacancyLevel && (
          <>
            <Stack w="100%" gap={0}>
              <FormLabel fontSize="14px" fontWeight="semibold">
                Linha de pesquisa:
              </FormLabel>
              <CustomSelect
                options={researchLines.data ?? []}
                value={selectedResearchLineId || ''}
                disabled={!selectedLevelId || researchLines.isLoading || !researchLines.data?.length}
                setValue={(value) => {
                  hookForms.setValue('research_line', value);
                  hookForms.setValue('thematic_axis', '');
                  hookForms.setValue('vacancy', '');
                }}
              />
            </Stack>
            <Stack w="100%" gap={0}>
              <FormLabel fontSize="14px" fontWeight="semibold">
                Eixo temático:
              </FormLabel>
              <CustomSelect
                options={thematicAxes.data ?? []}
                value={selectedThematicAxisId || ''}
                disabled={!selectedResearchLineId || thematicAxes.isLoading || !thematicAxes.data?.length}
                setValue={(value) => {
                  hookForms.setValue('thematic_axis', value);
                  hookForms.setValue('vacancy', '');
                }}
              />
            </Stack>
          </>
        )}
        <Stack w="100%" flex="0 0 100%" gap={0}>
          <FormLabel fontSize="14px" fontWeight="semibold">
            Selecione a sua vaga/cargo desejado:{' '}
            <Text as="span" color="red.500">
              *
            </Text>
          </FormLabel>

          <CustomSelect
            options={vacancyOptions}
            value={hookForms.watch('vacancy') || ''}
            setValue={(val) => {
              hookForms.setValue('vacancy', val);
              hookForms.setValue('type_of_competition', '');
              hookForms.setValue('type_of_competition_id', '');
              hookForms.setValue('modality_type', undefined);
            }}
            placeholder="Selecione..."
            disabled={
              Boolean(isFilterVacancyLevel && !selectedLevelId) ||
              researchLines.isLoading ||
              vacancyOptions.length === 0 ||
              (hasResearchLineCascade ? !selectedThematicAxisId || filteredVacancies.isLoading : !listVacancy?.length)
            }
          />

          {showNoVacanciesMessage && (
            <Text mt={2} fontSize="sm" color="red.500">
              Não há vagas disponíveis para essa seleção
            </Text>
          )}

          <MessageError>{hookForms.formState.errors?.vacancy?.message}</MessageError>
        </Stack>
      </Flex>

      <FormControl isRequired>
        <FormLabel fontSize="14px" fontWeight="semibold">
          Tipo de competição:
        </FormLabel>

        <Select
          required={false}
          {...hookForms.register('type_of_competition', {
            onChange: (event) => {
              const selectedType = event.currentTarget.value;
              const selectedTypeId = selectedVacancy?.type_of_competition.find((type) => {
                const normalizedName = type.name.toLowerCase().replace(/[-_]/g, ' ');
                if (selectedType === ETypeOfCompetition.FULL_VACANCY) return normalizedName.includes('ampla');
                if (selectedType === ETypeOfCompetition.UFC_SERVER_VACANCY) return normalizedName.includes('ufc');
                return false;
              })?.id;

              hookForms.setValue('type_of_competition_id', selectedTypeId ?? '', {
                shouldDirty: true,
              });

              if (event.currentTarget.value === ETypeOfCompetition.FULL_VACANCY) {
                hookForms.setValue('modality_type', undefined);
              }
            },
          })}
          disabled={!hookForms.watch('vacancy') || !hasCompetitionOptions}
        >
          <option value="" hidden>
            Selecione...
          </option>
          {showFullVacancies && <option value={ETypeOfCompetition.FULL_VACANCY}>Ampla concorrência</option>}
          {showReservedVacancies && (
            <option value={ETypeOfCompetition.SPECIAL_VACANCY}>
              {isFilterVacancyLevel ? 'PAA' : 'Vagas reservadas'}
            </option>
          )}
          {showUfcServerVacancies && <option value={ETypeOfCompetition.UFC_SERVER_VACANCY}>S-UFC</option>}
        </Select>

        <MessageError>{hookForms.formState.errors?.type_of_competition?.message}</MessageError>
      </FormControl>

      {modalityAvailable && hookForms.watch('type_of_competition') === ETypeOfCompetition.SPECIAL_VACANCY && (
        <FormControl isRequired isInvalid={!!hookForms.formState.errors.modality_type?.message}>
          <FormLabel fontSize="14px" fontWeight="semibold">
            Selecione sua modalidade de concorrência
          </FormLabel>

          <Stack>
            {modalityAvailable
              .filter((m) => {
                const normalizedName = m.name.toLowerCase().replace(/[-_]/g, ' ');
                if (normalizedName.includes('ampla')) return false;
                if (normalizedName.includes('ufc')) return false;
                return true;
              })
              .map((modality) => {
                const isChecked = isModalityChecked(modality);

                return (
                  <Stack key={modality.id}>
                    <Checkbox
                      isChecked={isChecked}
                      required={false}
                      onChange={(e) => {
                        const value = JSON.stringify(modality);
                        const current = hookForms.watch('modality_type') ?? [];

                        const withoutSelected = current.filter((item) => {
                          try { return JSON.parse(item).id !== modality.id; }
                          catch { return item !== modality.id; }
                        });
                        const newValues = e.target.checked
                          ? !isFilterVacancyLevel
                            ? [...withoutSelected, value]
                            : [value]
                          : withoutSelected;

                        if (e.target.checked && isFilterVacancyLevel) {
                          if (modality.name.toLowerCase().includes('pcd')) {
                            hookForms.setValue('sub_type_of_competition', []);
                          } else if (modality.name.toLowerCase().includes('pppiq')) {
                            hookForms.setValue('medical_report', undefined);
                            hookForms.setValue('cid', '');
                            hookForms.setValue('deficiency', '');
                          }
                        }

                        hookForms.setValue('modality_type', newValues, {
                          shouldDirty: true,
                          shouldValidate: true,
                        });
                      }}
                    >
                      {modality.name}
                    </Checkbox>

                    {isChecked && modality.name.toLowerCase().includes('pcd') && renderPCDBlock()}
                    {isChecked && modality.name.toLowerCase().includes('pppiq') && renderPPPIQBlock()}
                  </Stack>
                );
              })}
          </Stack>

          <MessageError>{hookForms.formState.errors.modality_type?.message}</MessageError>
        </FormControl>
      )}
    </Stack>
  );
};

export default DefaultVacancyForm;
